<template>
  <div class="fixed-point-map" :style="containerStyle">
    <div :id="mapId" class="fixed-point-map__canvas"></div>
    <div v-if="errorMessage" class="fixed-point-map__error">{{ errorMessage }}</div>
  </div>
</template>

<script>
/* global T */

const X_PI = (Math.PI * 3000.0) / 180.0
const COORDINATE_PI = 3.1415926535897932384626
const EARTH_SEMI_MAJOR_AXIS = 6378245.0
const ECCENTRICITY_SQUARED = 0.00669342162296594323

// 判断坐标是否位于中国大陆范围之外。
function isOutOfChina(longitude, latitude) {
  return longitude < 72.004 || longitude > 137.8347 || latitude < 0.8293 || latitude > 55.8271
}

// 根据 GCJ-02 转换公式计算经度偏移量。
function transformLongitude(longitude, latitude) {
  let result =
    300.0 +
    longitude +
    2.0 * latitude +
    0.1 * longitude * longitude +
    0.1 * longitude * latitude +
    0.1 * Math.sqrt(Math.abs(longitude))
  result +=
    ((20.0 * Math.sin(6.0 * longitude * COORDINATE_PI) + 20.0 * Math.sin(2.0 * longitude * COORDINATE_PI)) * 2.0) / 3.0
  result +=
    ((20.0 * Math.sin(longitude * COORDINATE_PI) + 40.0 * Math.sin((longitude / 3.0) * COORDINATE_PI)) * 2.0) / 3.0
  result +=
    ((150.0 * Math.sin((longitude / 12.0) * COORDINATE_PI) + 300.0 * Math.sin((longitude / 30.0) * COORDINATE_PI)) *
      2.0) /
    3.0
  return result
}

// 根据 GCJ-02 转换公式计算纬度偏移量。
function transformLatitude(longitude, latitude) {
  let result =
    -100.0 +
    2.0 * longitude +
    3.0 * latitude +
    0.2 * latitude * latitude +
    0.1 * longitude * latitude +
    0.2 * Math.sqrt(Math.abs(longitude))
  result +=
    ((20.0 * Math.sin(6.0 * longitude * COORDINATE_PI) + 20.0 * Math.sin(2.0 * longitude * COORDINATE_PI)) * 2.0) / 3.0
  result +=
    ((20.0 * Math.sin(latitude * COORDINATE_PI) + 40.0 * Math.sin((latitude / 3.0) * COORDINATE_PI)) * 2.0) / 3.0
  result +=
    ((160.0 * Math.sin((latitude / 12.0) * COORDINATE_PI) + 300.0 * Math.sin((latitude * COORDINATE_PI) / 30.0)) *
      2.0) /
    3.0
  return result
}

// 将 WGS-84 坐标转换为 GCJ-02 坐标。
function wgs84ToGcj02(longitude, latitude) {
  if (isOutOfChina(longitude, latitude)) {
    return [longitude, latitude]
  }

  let deltaLatitude = transformLatitude(longitude - 105.0, latitude - 35.0)
  let deltaLongitude = transformLongitude(longitude - 105.0, latitude - 35.0)
  const radianLatitude = (latitude / 180.0) * COORDINATE_PI
  let magic = Math.sin(radianLatitude)
  magic = 1 - ECCENTRICITY_SQUARED * magic * magic
  const sqrtMagic = Math.sqrt(magic)
  deltaLatitude =
    (deltaLatitude * 180.0) /
    (((EARTH_SEMI_MAJOR_AXIS * (1 - ECCENTRICITY_SQUARED)) / (magic * sqrtMagic)) * COORDINATE_PI)
  deltaLongitude =
    (deltaLongitude * 180.0) / ((EARTH_SEMI_MAJOR_AXIS / sqrtMagic) * Math.cos(radianLatitude) * COORDINATE_PI)

  return [longitude + deltaLongitude, latitude + deltaLatitude]
}

// 使用迭代法将 GCJ-02 坐标反向转换为 WGS-84 坐标。
function gcj02ToWgs84(longitude, latitude) {
  if (isOutOfChina(longitude, latitude)) {
    return [longitude, latitude]
  }

  let wgsLongitude = longitude
  let wgsLatitude = latitude
  for (let index = 0; index < 10; index += 1) {
    const gcjCoordinates = wgs84ToGcj02(wgsLongitude, wgsLatitude)
    const deltaLongitude = gcjCoordinates[0] - longitude
    const deltaLatitude = gcjCoordinates[1] - latitude
    wgsLongitude -= deltaLongitude
    wgsLatitude -= deltaLatitude

    if (Math.abs(deltaLongitude) < 1e-7 && Math.abs(deltaLatitude) < 1e-7) {
      break
    }
  }

  return [wgsLongitude, wgsLatitude]
}

// 将百度 BD-09 坐标转换为 GCJ-02 坐标。
function bd09ToGcj02(bdLng, bdLat) {
  const x = Number(bdLng) - 0.0065
  const y = Number(bdLat) - 0.006
  const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * X_PI)
  const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * X_PI)

  return [z * Math.cos(theta), z * Math.sin(theta)]
}

export default {
  name: 'FixedPointMap',

  props: {
    longitude: {
      type: Number,
      required: true,
    },
    latitude: {
      type: Number,
      required: true,
    },
    labelText: {
      type: String,
      required: true,
    },
    coordinateSystem: {
      type: String,
      default: 'wgs84',
      // 限制坐标系参数只能使用组件支持的三种类型。
      validator: (value) => ['wgs84', 'gcj02', 'bd09'].includes(value),
    },
    zoom: {
      type: Number,
      default: 15,
    },
    height: {
      type: String,
      default: '400px',
    },
  },

  // 初始化组件状态和地图实例引用。
  data() {
    return {
      errorMessage: '',
      label: null,
      map: null,
      mapId: `fixed-point-map-${this._uid}`,
      marker: null,
      sdkTimer: null,
    }
  },

  computed: {
    // 将传入的高度转换为地图容器的内联样式。
    containerStyle() {
      return { height: this.height }
    },
  },

  watch: {
    // 坐标经度变化时重新定位地图和覆盖物。
    longitude() {
      this.updatePoint()
    },
    // 坐标纬度变化时重新定位地图和覆盖物。
    latitude() {
      this.updatePoint()
    },
    // 坐标系变化时重新计算地图坐标。
    coordinateSystem() {
      this.updatePoint()
    },
    // 标注文本变化时重新绘制标注。
    labelText() {
      this.updateLabel()
    },
    // 缩放级别变化时更新地图缩放。
    zoom() {
      this.updateZoom()
    },
  },

  // 组件挂载后开始等待地图 SDK 并初始化地图。
  mounted() {
    this.initializeMapWhenReady()
  },

  // 组件销毁前停止轮询、移除覆盖物并释放地图实例。
  beforeDestroy() {
    this.stopSdkPolling()
    this.removeOverlays()

    if (this.map) {
      this.map.destroy()
      this.map = null
    }
  },

  methods: {
    // 判断地图 SDK 是否已经具备组件初始化所需的全部构造器。
    isSdkReady() {
      return (
        typeof T !== 'undefined' &&
        typeof T.Map === 'function' &&
        typeof T.LngLat === 'function' &&
        typeof T.Point === 'function' &&
        typeof T.Marker === 'function' &&
        typeof T.Label === 'function' &&
        T.Control &&
        typeof T.Control.Scale === 'function' &&
        typeof T.Control.Zoom === 'function'
      )
    },

    // 校验坐标并等待天地图 SDK 就绪后创建地图。
    initializeMapWhenReady() {
      if (!this.hasValidCoordinates()) {
        this.reportError('INVALID_COORDINATES', '经纬度必须是有效的地理坐标。')
        return
      }

      if (this.isSdkReady()) {
        this.createMap()
        return
      }

      let attempts = 0
      // 定时检查 SDK 是否已经完成加载，避免直接访问未定义的全局对象。
      this.sdkTimer = setInterval(() => {
        attempts += 1

        if (this.isSdkReady()) {
          this.stopSdkPolling()
          this.createMap()
        } else if (attempts >= 50) {
          this.stopSdkPolling()
          this.reportError('SDK_TIMEOUT', '天地图 SDK 加载超时。')
        }
      }, 200)
    },

    // 创建地图实例、添加基础控件并绘制业务覆盖物。
    createMap() {
      if (this.map) return

      const point = this.getMapPoint()
      this.map = new T.Map(this.mapId, {
        center: point,
        zoom: this.zoom,
      })
      this.map.addControl(new T.Control.Scale())
      this.map.addControl(new T.Control.Zoom())
      this.renderOverlays()
      this.$emit('ready', { map: this.map, marker: this.marker, label: this.label })
    },

    // 按组件声明的坐标系将输入坐标转换为天地图使用的坐标。
    getMapPoint() {
      const longitude = Number(this.longitude)
      const latitude = Number(this.latitude)
      let coordinates = [longitude, latitude]

      if (this.coordinateSystem === 'bd09') {
        coordinates = bd09ToGcj02(longitude, latitude)
        coordinates = gcj02ToWgs84(coordinates[0], coordinates[1])
      } else if (this.coordinateSystem === 'gcj02') {
        coordinates = gcj02ToWgs84(longitude, latitude)
      }

      return new T.LngLat(coordinates[0], coordinates[1])
    },

    // 检查经纬度是否为有限数字且处于合法地理范围内。
    hasValidCoordinates() {
      const longitude = Number(this.longitude)
      const latitude = Number(this.latitude)

      return (
        Number.isFinite(longitude) &&
        Number.isFinite(latitude) &&
        longitude >= -180 &&
        longitude <= 180 &&
        latitude >= -90 &&
        latitude <= 90
      )
    },

    // 根据当前坐标创建并添加标记点和文本标注。
    renderOverlays() {
      if (!this.map || !this.hasValidCoordinates()) return

      this.removeOverlays()
      const point = this.getMapPoint()

      this.marker = new T.Marker(point, { draggable: false })
      this.label = new T.Label({
        style: {
          backgroundColor: '#ffffff',
          border: '1px solid #8ca0b3',
          borderRadius: '4px',
          boxShadow: '0 1px 4px rgba(15, 23, 42, 0.22)',
          color: '#172033',
          fontSize: '14px',
          lineHeight: '20px',
          maxWidth: '240px',
          padding: '8px 10px',
          textAlign: 'center',
          whiteSpace: 'normal',
        },
        text: this.labelText,
        position: point,
        offset: new T.Point(-120, -60),
      })

      this.map.addOverLay(this.marker)
      this.map.addOverLay(this.label)
    },

    // 从地图中移除当前标记点和文本标注。
    removeOverlays() {
      if (!this.map) return

      if (this.marker) {
        this.map.removeOverLay(this.marker)
        this.marker = null
      }
      if (this.label) {
        this.map.removeOverLay(this.label)
        this.label = null
      }
    },

    // 处理坐标或坐标系变化，重新定位地图并刷新覆盖物。
    updatePoint() {
      if (!this.hasValidCoordinates()) {
        this.removeOverlays()
        this.reportError('INVALID_COORDINATES', '经纬度必须是有效的地理坐标。')
        return
      }
      if (!this.map) return

      this.errorMessage = ''
      const point = this.getMapPoint()
      this.map.centerAndZoom(point, this.zoom)
      this.renderOverlays()
    },

    // 处理标注文本变化，重新绘制文本标注。
    updateLabel() {
      if (!this.map) return

      this.renderOverlays()
    },

    // 处理缩放级别变化并保持地图中心点不变。
    updateZoom() {
      if (!this.map || !this.hasValidCoordinates()) return

      this.map.centerAndZoom(this.getMapPoint(), this.zoom)
    },

    // 清理等待地图 SDK 的定时器。
    stopSdkPolling() {
      if (this.sdkTimer) {
        clearInterval(this.sdkTimer)
        this.sdkTimer = null
      }
    },

    // 保存错误信息并向父组件派发错误事件。
    reportError(code, message) {
      this.errorMessage = message
      this.$emit('error', { code, message })
    },
  },
}
</script>

<style scoped>
.fixed-point-map {
  min-height: 240px;
  position: relative;
  width: 100%;
}

.fixed-point-map__canvas {
  height: 100%;
  width: 100%;
}

.fixed-point-map__error {
  align-items: center;
  background: #ffffff;
  color: #9b1c1c;
  display: flex;
  font-size: 14px;
  inset: 0;
  justify-content: center;
  position: absolute;
}
</style>
